import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { ArrowRight, CheckCircle2, MessageCircle, Phone, Clock3, ShieldCheck, Download, Sparkles, Paperclip, X } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import { Reveal } from "../components/motion";
import useMeta from "../lib/useMeta";
import { breadcrumb } from "../lib/seo";
import { SERVICES, CONTACT, CATALOGUE_URL, REQUIREMENT_TYPES, RESPONSE_PROMISE, waLink } from "../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  name: "", company: "", designation: "", phone: "", email: "", city: "",
  locations: "", requirement_type: "", details: "", approx_spend: "",
  timeline: "", contact_method: "WhatsApp",
};

const LOCATION_OPTIONS = ["1", "2-5", "6-20", "20+"];
const TIMELINE_OPTIONS = ["Urgent — this week", "Within 2 weeks", "Within a month", "Flexible"];

export default function RequestQuote() {
  useMeta({
    title: "Request a Quote — Corporate Procurement Services India | Saarthva",
    description: "Send Saarthva your procurement or operations requirement — one-time purchase, recurring procurement, managed procurement, operations support, new-site setup or custom sourcing.",
    path: "/request-quote",
    schemas: [breadcrumb("Request a Quote", "/request-quote")],
  });
  const [params] = useSearchParams();
  const categoryTitle = SERVICES.find((s) => s.id === params.get("category"))?.title;
  const [form, setForm] = useState({
    ...EMPTY,
    requirement_type: params.get("type") || "",
    details: categoryTitle ? `Category: ${categoryTitle} — ` : "",
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [aiText, setAiText] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const [attBusy, setAttBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const fillWithAI = async () => {
    if (!aiText.trim() || aiBusy) return;
    setAiBusy(true);
    try {
      const res = await axios.post(`${API}/quote/parse`, { text: aiText });
      setForm((f) => ({
        ...f,
        requirement_type: res.data.requirement_type || f.requirement_type,
        details: res.data.details || f.details,
        city: res.data.city || f.city,
      }));
      toast.success("Form pre-filled by AI — review and hit submit.");
    } catch {
      toast.error("AI helper unavailable right now — please fill the form manually.");
    } finally {
      setAiBusy(false);
    }
  };

  const uploadAttachment = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAttBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/uploads`, fd);
      setAttachment({ path: res.data.path, name: res.data.filename });
      toast.success("Attachment uploaded.");
    } catch {
      toast.error("Upload failed — you can still submit without it.");
    } finally {
      setAttBusy(false);
    }
  };

  const waMessage = () =>
    `Hi Saarthva, I'm ${form.name}${form.company ? ` from ${form.company}` : ""} (${form.city || "India"}). Requirement type: ${form.requirement_type || "General"}. Details: ${form.details || "-"}. You can reach me at ${form.phone} / ${form.email}.`;

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/quotes`, {
        ...form,
        attachment_path: attachment?.path || "",
        attachment_name: attachment?.name || "",
      });
      setSent(true);
      toast.success("Quote request received — a Saarthi will reach out shortly.");
    } catch {
      toast.error("Submission failed. Please use WhatsApp or call us directly.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-line bg-white px-4 py-3 text-sm text-navy placeholder:text-slate-400 outline-none transition-colors focus:border-steel focus:ring-2 focus:ring-steel/15";

  return (
    <div data-testid="request-quote-page">
      <PageHeader
        eyebrow="REQUEST A QUOTE"
        title="Tell us what you need."
        accent="We handle the rest."
        description="One form, one accountable partner. Share your requirement — from a single item to a full site setup."
      />

      <section className="bg-white" data-testid="quote-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-5 gap-12">
          <div className="lg:col-span-2">
            <Reveal>
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-navy leading-tight">What happens next?</h2>
              <div className="mt-8 space-y-6">
                {[
                  { icon: Clock3, t: "Target response in 4 business hours", d: "During business hours, a Saarthi reviews your requirement and responds on your preferred channel." },
                  { icon: ShieldCheck, t: "Sourcing & comparison", d: "We identify suitable vendors and compare commercials, lead time and quality parameters." },
                  { icon: MessageCircle, t: "One curated quotation", d: "No spam, no ten follow-up calls — one clear recommendation for your approval." },
                ].map((s, i) => {
                  const Icon = s.icon;
                  return (
                    <div key={i} className="flex gap-4" data-testid={`quote-step-${i}`}>
                      <span className="w-11 h-11 rounded-xl bg-cream grid place-items-center shrink-0">
                        <Icon className="w-5 h-5 text-saffron" strokeWidth={1.8} />
                      </span>
                      <div>
                        <p className="font-display font-bold text-navy">{s.t}</p>
                        <p className="text-sm text-slate-600 mt-1 leading-relaxed">{s.d}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-10 rounded-2xl bg-navy text-white p-6" data-testid="quote-direct-contact">
                <p className="font-mono text-[11px] tracking-[0.3em] text-saffron mb-3">PREFER TALKING?</p>
                <a href={`tel:+${CONTACT.phoneIntl}`} data-testid="quote-phone-link" className="flex items-center gap-2 font-display text-xl font-bold hover:text-saffron transition-colors">
                  <Phone className="w-5 h-5 text-saffron" /> {CONTACT.phoneDisplay}
                </a>
                <a href={waLink("Hi Saarthva, I'd like a quote for:")} target="_blank" rel="noopener noreferrer" data-testid="quote-whatsapp-link" className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-slate-300 hover:text-white transition-colors">
                  <MessageCircle className="w-4 h-4 text-saffron" /> Or WhatsApp your requirement directly
                </a>
              </div>
              <a href={CATALOGUE_URL} data-testid="quote-catalogue-download" className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-steel hover:text-navy transition-colors">
                <Download className="w-4 h-4" /> Download our one-page line card (PDF)
              </a>
            </Reveal>
          </div>

          <Reveal delay={0.1} className="lg:col-span-3">
            <div className="rounded-3xl border border-line bg-mist p-7 sm:p-10" data-testid="quote-form-card">
              {sent ? (
                <div className="text-center py-12" data-testid="quote-success">
                  <CheckCircle2 className="w-16 h-16 text-saffron mx-auto mb-5" strokeWidth={1.5} />
                  <h2 className="font-display text-2xl sm:text-3xl font-bold text-navy">Request received.</h2>
                  <p className="mt-3 text-slate-600 max-w-md mx-auto">
                    Thank you, {form.name.split(" ")[0]}. {RESPONSE_PROMISE}
                  </p>
                  <div className="mt-8 flex flex-wrap justify-center gap-4">
                    <a
                      href={waLink(waMessage())}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="quote-success-whatsapp-button"
                      className="inline-flex items-center gap-2 rounded-full bg-[#25D366] hover:brightness-95 text-white font-semibold px-7 py-3.5 transition-all"
                    >
                      <MessageCircle className="w-4 h-4" /> Also send on WhatsApp
                    </a>
                    <button
                      onClick={() => { setSent(false); setForm(EMPTY); setAttachment(null); }}
                      data-testid="quote-submit-another-button"
                      className="rounded-full border border-line bg-white hover:border-steel text-navy font-semibold px-7 py-3.5 transition-colors"
                    >
                      Submit another request
                    </button>
                  </div>
                </div>
              ) : (
                <>
                <div className="mb-7 rounded-2xl border-2 border-dashed border-steel/30 bg-white p-5" data-testid="ai-helper-card">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="w-4 h-4 text-saffron" />
                    <p className="text-sm font-bold text-navy">Describe it in plain words — AI fills the form</p>
                  </div>
                  <textarea
                    rows={2}
                    value={aiText}
                    onChange={(e) => setAiText(e.target.value)}
                    placeholder="e.g. Need 40 ergonomic chairs and 20 desks for our new Noida office next month"
                    className={inputCls}
                    data-testid="ai-helper-input"
                  />
                  <button
                    type="button"
                    onClick={fillWithAI}
                    disabled={aiBusy || !aiText.trim()}
                    data-testid="ai-helper-button"
                    className="mt-3 inline-flex items-center gap-2 rounded-full bg-navy hover:bg-saffron disabled:opacity-60 text-white text-sm font-semibold px-5 py-2.5 transition-colors"
                  >
                    <Sparkles className="w-4 h-4" /> {aiBusy ? "Thinking..." : "Fill with AI"}
                  </button>
                </div>
                <form onSubmit={submit} className="space-y-5" data-testid="quote-form">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-semibold text-navy mb-1.5">Name *</label>
                      <input required value={form.name} onChange={set("name")} placeholder="Your full name" className={inputCls} data-testid="quote-input-name" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-navy mb-1.5">Company *</label>
                      <input required value={form.company} onChange={set("company")} placeholder="Company name" className={inputCls} data-testid="quote-input-company" />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-semibold text-navy mb-1.5">Designation</label>
                      <input value={form.designation} onChange={set("designation")} placeholder="e.g. Admin Head" className={inputCls} data-testid="quote-input-designation" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-navy mb-1.5">Phone *</label>
                      <input required type="tel" value={form.phone} onChange={set("phone")} placeholder="+91 ..." className={inputCls} data-testid="quote-input-phone" />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-semibold text-navy mb-1.5">Work Email *</label>
                      <input required type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" className={inputCls} data-testid="quote-input-email" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-navy mb-1.5">City *</label>
                      <input required value={form.city} onChange={set("city")} placeholder="e.g. Gurugram" className={inputCls} data-testid="quote-input-city" />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-semibold text-navy mb-1.5">Number of Locations</label>
                      <select value={form.locations} onChange={set("locations")} className={inputCls} data-testid="quote-select-locations">
                        <option value="">Select</option>
                        {LOCATION_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-navy mb-1.5">Preferred Timeline</label>
                      <select value={form.timeline} onChange={set("timeline")} className={inputCls} data-testid="quote-select-timeline">
                        <option value="">Select</option>
                        {TIMELINE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-navy mb-2.5">Requirement Type *</label>
                    <div className="flex flex-wrap gap-2" data-testid="quote-type-group">
                      {REQUIREMENT_TYPES.map((t) => (
                        <button
                          type="button"
                          key={t}
                          onClick={() => setForm({ ...form, requirement_type: t })}
                          data-testid={`quote-type-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                          className={`rounded-full px-4 py-2 text-[13px] font-semibold border transition-colors ${
                            form.requirement_type === t
                              ? "bg-navy text-white border-navy"
                              : "bg-white text-slate-600 border-line hover:border-steel"
                          }`}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                    {!form.requirement_type && <input tabIndex={-1} required value={form.requirement_type} onChange={() => {}} className="sr-only" aria-hidden="true" data-testid="quote-type-required" />}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-navy mb-1.5">Requirement Description *</label>
                    <textarea required rows={4} value={form.details} onChange={set("details")} placeholder="e.g. 50 units of ergonomic chairs for a new office in Gurugram, needed by next month..." className={inputCls} data-testid="quote-input-details" />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-semibold text-navy mb-1.5">Approx. Quantity / Monthly Spend <span className="text-slate-400 font-normal">(optional)</span></label>
                      <input value={form.approx_spend} onChange={set("approx_spend")} placeholder="e.g. 50 units or ₹1L / month" className={inputCls} data-testid="quote-input-approx-spend" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-navy mb-1.5">Attachment <span className="text-slate-400 font-normal">(optional)</span></label>
                      {attachment ? (
                        <div className="flex items-center gap-2 rounded-xl border border-line bg-white px-4 py-3" data-testid="quote-attachment-chip">
                          <Paperclip className="w-4 h-4 text-saffron shrink-0" />
                          <span className="text-sm text-navy truncate flex-1">{attachment.name}</span>
                          <button type="button" onClick={() => setAttachment(null)} aria-label="Remove attachment" data-testid="quote-attachment-remove" className="text-slate-400 hover:text-navy">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <label className={`flex items-center gap-2 rounded-xl border border-dashed border-line bg-white px-4 py-3 text-sm text-slate-500 cursor-pointer hover:border-steel transition-colors ${attBusy ? "opacity-60" : ""}`} data-testid="quote-attachment-upload">
                          <Paperclip className="w-4 h-4 text-saffron shrink-0" />
                          {attBusy ? "Uploading..." : "Attach a file (PDF, image, doc — max 10 MB)"}
                          <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xlsx,.csv,.txt" onChange={uploadAttachment} disabled={attBusy} />
                        </label>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-navy mb-2.5">Preferred Contact *</label>
                    <div className="flex flex-wrap gap-3" data-testid="quote-contact-method-group">
                      {["WhatsApp", "Phone", "Email"].map((m) => (
                        <button
                          type="button"
                          key={m}
                          onClick={() => setForm({ ...form, contact_method: m })}
                          data-testid={`quote-contact-method-${m.toLowerCase()}`}
                          className={`rounded-full px-5 py-2.5 text-sm font-semibold border transition-colors ${
                            form.contact_method === m
                              ? "bg-navy text-white border-navy"
                              : "bg-white text-slate-600 border-line hover:border-steel"
                          }`}
                        >
                          {m}
                        </button>
                      ))}
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={sending}
                    data-testid="quote-submit-button"
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-saffron hover:bg-saffrondark disabled:opacity-60 text-white font-semibold px-10 py-4 transition-colors"
                  >
                    {sending ? "Submitting..." : "Submit Quote Request"} <ArrowRight className="w-4 h-4" />
                  </button>
                  <p className="text-xs text-slate-500">No spam, no obligation. Your details are used only to respond to this request.</p>
                </form>
                </>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
